import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons/faEllipsisH';

import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';

import { useModal } from '../../../../hooks/use-modal';
import { WalletRegistrationOrUpdateButton } from '../../../../wallet/components/wallet-registration-or-update-button';
import { useWallet } from '../../../../wallet/hooks/use-wallet';
import { emptyObject } from 'ui-components/default-props/empty';
import { walletSettingsEnum, walletSellerProviderSettingsEnum } from '../../../../enums/wallet-settings';
import { refreshLabelProviders } from '../../../../store/seller/seller.actions';
import {
  useWalletSellerLabelProviderSetting,
  useCanProviderViewStampsReports,
  useWalletSetting,
} from '../../../../wallet/contexts/wallet-data-context-provider';
import { getSellerProviderAccountLink, refreshSellerProviderServices, getWalletReportLink } from '../../../../wallet/utilities/api';
import { useEnableBalanceHistory } from '../../../../feature-flags/hooks/use-enable-balance-history';
import { useServiceVisibilityContext } from '../../providers/service-visibility-modal/context-provider';
import updateSellerProviderSettings from '../../providers/shipstation-settings-modal/api';
import UpdatePasswordModal from '../../providers/providers-list/wallets/update-password-modal';
import { EventSourceStrings } from '../../../../tracking/tracking-constants';
import { useSellerLabelProviders } from '../../../../hooks/use-seller-label-providers';

import messages from '../messages';
import walletStyle from '../../providers/providers-list/wallets/style.module.less';

const ActionsDropdown = ({ children, keepOpen = false }) => {
  return (
    <DropdownMenu closeWhenChildrenClicked={!keepOpen} menuPosition='bottom-right'>
      <DropdownMenuToggler className={walletStyle['menu-toggle']} hideChevron theme='link'>
        <div className={walletStyle['menu-toggle-contents']}>
          <FontAwesomeIcon icon={faEllipsisH} />
        </div>
      </DropdownMenuToggler>
      {children}
    </DropdownMenu>
  );
};

const RegisteredProviderActions = ({ walletId, sellerLabelProvider, isActiveCarrier }) => {
  const { labelProviderId, sellerLabelProviderId, isPrimaryAccount } = sellerLabelProvider;

  const enableBalanceHistory = useEnableBalanceHistory();
  const canViewStampsReports = useCanProviderViewStampsReports(walletId, labelProviderId);
  const { canUseSetting: allowBalanceHistory } = useWalletSetting(walletId, walletSettingsEnum.balanceHistory, enableBalanceHistory);
  const [reportsLink, setReportsLink] = useAsyncSafeState(undefined);
  const sellerLabelProvidersForProvider = useSellerLabelProviders(labelProviderId);
  const hasMultipleAccountsForProvider = sellerLabelProvidersForProvider.length > 1;

  const dispatch = useDispatch();
  const [settingsLink, setSettingsLink] = useAsyncSafeState();
  const { isOpen: isPasswordModalOpen, open: openPasswordModal, close: closePasswordModal } = useModal();
  const { canUseSetting: canRefreshServices } = useWalletSellerLabelProviderSetting(
    walletId,
    sellerLabelProviderId,
    walletSellerProviderSettingsEnum.refreshServices,
  );
  const { canUseSetting: canGetSettingsLink } = useWalletSellerLabelProviderSetting(
    walletId,
    sellerLabelProviderId,
    walletSellerProviderSettingsEnum.accountSettingsLink,
  );
  const { canUseSetting: canUpdatePassword } = useWalletSellerLabelProviderSetting(
    walletId,
    sellerLabelProviderId,
    walletSellerProviderSettingsEnum.updatePassword,
  );
  const { canUseSetting: canSetAsDefault } = useWalletSellerLabelProviderSetting(
    walletId,
    sellerLabelProviderId,
    walletSellerProviderSettingsEnum.setAsPreferredAccount,
  );
  const { onManageServices } = useServiceVisibilityContext();
  const openServiceModal = useCallback(
    () => onManageServices(labelProviderId, sellerLabelProviderId),
    [labelProviderId, onManageServices, sellerLabelProviderId],
  );

  const setPrimaryAccount = useCallback(() => {
    updateSellerProviderSettings(sellerLabelProviderId, null, true)
      .then(() => {
        dispatch(refreshLabelProviders({ shouldRefreshServices: true }));
      })
      .catch(() => {
        NotificationService.showNotification({
          content: (
            <FormattedMessage
              defaultMessage='An unexpected error occurred trying to set a default account. Please try again.'
              description='error message when unable to set a carrier account as default'
              id='aKq2zB'
            />
          ),
          notificationType: NOTIFICATION_TYPE_ERROR,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      });
  }, [dispatch, sellerLabelProviderId]);

  const refreshServices = useCallback(() => {
    refreshSellerProviderServices(sellerLabelProviderId)
      .then(() => {
        dispatch(refreshLabelProviders({ shouldRefreshServices: true }));
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          content: <FormattedMessage {...messages.refreshServiceSuccess} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      })
      .catch(() => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.refreshServiceError} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      });
  }, [sellerLabelProviderId, dispatch]);

  useEffect(() => {
    if (!canGetSettingsLink) {
      return;
    }
    getSellerProviderAccountLink(sellerLabelProviderId)
      .then(({ sellerProviderAccountUrl }) => setSettingsLink(sellerProviderAccountUrl))
      .catch(() => {
        setSettingsLink(undefined);
      });
  }, [canGetSettingsLink, setSettingsLink, sellerLabelProviderId]);

  useEffect(() => {
    if (!canViewStampsReports || allowBalanceHistory) {
      return;
    }
    getWalletReportLink(walletId)
      .then((link) => setReportsLink(link))
      .catch(() => setReportsLink(undefined));
  }, [canViewStampsReports, setReportsLink, walletId, allowBalanceHistory]);

  return (
    <>
      <ActionsDropdown>
        {reportsLink && (
          <DropdownMenuItem className={walletStyle['dropdown-menu-link-item']} disabled={!reportsLink} href={reportsLink}>
            <FormattedMessage {...messages.viewReports} />
          </DropdownMenuItem>
        )}
        {canGetSettingsLink && (
          <DropdownMenuItem className={walletStyle['dropdown-menu-link-item']} disabled={!settingsLink} href={settingsLink}>
            <FormattedMessage {...messages.openSettings} />
          </DropdownMenuItem>
        )}
        {canSetAsDefault && hasMultipleAccountsForProvider && (
          <DropdownMenuItem disabled={isPrimaryAccount} onClick={setPrimaryAccount}>
            <FormattedMessage {...messages.setAsDefault} />
          </DropdownMenuItem>
        )}
        {canUpdatePassword && (
          <DropdownMenuItem onClick={openPasswordModal}>
            <FormattedMessage {...messages.reauthorize} />
          </DropdownMenuItem>
        )}
        {canRefreshServices && (
          <DropdownMenuItem onClick={refreshServices}>
            <FormattedMessage {...messages.refreshServices} />
          </DropdownMenuItem>
        )}
        <DropdownMenuItem disabled={!isActiveCarrier} onClick={openServiceModal}>
          <FormattedMessage {...messages.manageServices} />
        </DropdownMenuItem>
      </ActionsDropdown>
      {isPasswordModalOpen && <UpdatePasswordModal onClose={closePasswordModal} sellerLabelProviderId={sellerLabelProviderId} />}
    </>
  );
};

const CarrierActions = ({ isActiveCarrier, walletId: walletIdProp = '', sellerLabelProvider = emptyObject }) => {
  const { walletId, isWalletRegistered = false } = useWallet(walletIdProp) ?? {};
  const providerRequiresSetup = sellerLabelProvider?.requiresSetup ?? true;

  if (!isWalletRegistered || providerRequiresSetup) {
    return (
      <ActionsDropdown keepOpen>
        <WalletRegistrationOrUpdateButton trackingId={EventSourceStrings.SETTINGS_CARRIERS_PAGE_TABLE} walletId={walletId}>
          {({ openModal }) => (
            <DropdownMenuItem onClick={openModal}>
              <FormattedMessage {...messages.activateCarrier} />
            </DropdownMenuItem>
          )}
        </WalletRegistrationOrUpdateButton>
      </ActionsDropdown>
    );
  }

  return <RegisteredProviderActions isActiveCarrier={isActiveCarrier} sellerLabelProvider={sellerLabelProvider} walletId={walletId} />;
};

CarrierActions.propTypes = {
  isActiveCarrier: PropTypes.bool.isRequired,
  walletId: PropTypes.string,
  sellerLabelProvider: PropTypes.shape({
    labelProviderId: PropTypes.string,
    sellerLabelProviderId: PropTypes.string,
    isPrimaryAccount: PropTypes.bool,
    requiresSetup: PropTypes.bool,
  }),
};

export default CarrierActions;
